import React, { createContext, useContext, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Task } from '../lib/tasks';

export interface TaskNotification {
  id: string;
  taskId: string;
  title: string;
  message: string;
  type: 'task_created' | 'task_updated' | 'task_completed';
  timestamp: Date;
  isRead: boolean;
  task: Task;
}

interface NotificationsContextType {
  notifications: TaskNotification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotification: (notificationId: string) => void;
  addNotification: (notification: TaskNotification) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);

  const addNotification = useCallback((notification: TaskNotification) => {
    setNotifications(prev => [notification, ...prev]);
  }, []);

  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, isRead: true }
          : notification
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, isRead: true }))
    );
  }, []);

  const clearNotification = useCallback((notificationId: string) => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== notificationId)
    );
  }, []);

  // Initialize Supabase real-time subscription
  React.useEffect(() => {
    if (!supabase) return;

    // Load existing tasks
    const loadExistingTasks = async () => {
      try {
        const { data: tasks, error } = await supabase
          .from('tasks')
          .select('*')
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (tasks) {
          console.log('Loaded existing tasks:', tasks);
          const taskNotifications: TaskNotification[] = tasks.map(task => ({
            id: crypto.randomUUID(),
            taskId: task.id,
            title: 'Task',
            message: `Task "${task.title}"`,
            type: 'task_created',
            timestamp: new Date(task.created_at),
            isRead: false,
            task: task
          }));
          setNotifications(prev => {
            // Merge with existing notifications, avoiding duplicates
            const existingTaskIds = new Set(prev.map(n => n.taskId));
            const newNotifications = taskNotifications.filter(n => !existingTaskIds.has(n.taskId));
            return [...prev, ...newNotifications];
          });
        }
      } catch (error) {
        console.error('Error loading existing tasks:', error);
      }
    };

    loadExistingTasks();

    const channel = supabase.channel('task-notifications');
    
    const subscription = channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'tasks'
        },
        (payload) => {
          console.log('New task created:', payload.new);
          const newTask = payload.new as Task;
          const notification: TaskNotification = {
            id: crypto.randomUUID(),
            taskId: newTask.id,
            title: 'New Task',
            message: `New task "${newTask.title}" has been created`,
            type: 'task_created',
            timestamp: new Date(),
            isRead: false,
            task: newTask
          };
          setNotifications(prev => [notification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tasks'
        },
        (payload) => {
          console.log('Task updated:', payload.new);
          const updatedTask = payload.new as Task;
          if (updatedTask.status === 'completed') {
            const notification: TaskNotification = {
              id: crypto.randomUUID(),
              taskId: updatedTask.id,
              title: 'Task Completed',
              message: `Task "${updatedTask.title}" has been completed`,
              type: 'task_completed',
              timestamp: new Date(),
              isRead: false,
              task: updatedTask
            };
            setNotifications(prev => [notification, ...prev]);
          }
        }
      );

    channel.subscribe((status) => {
      console.log('Subscription status:', status);
      if (status === 'SUBSCRIBED') {
        console.log('Successfully subscribed to task changes');
      }
    });

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const value = {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification,
    addNotification
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
} 